import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

import { TrackedProduct } from './useProducts';

export interface Wishlist {
  id: string;
  name: string;
  is_default: boolean;
  product_count?: number;
}

export interface WishlistDetail extends Wishlist {
  share_token?: string;
  products: TrackedProduct[];
}

interface ShareResult {
  share_token: string;
  share_url: string;
}

export function useWishlists() {
  return useQuery({
    queryKey: ['wishlists'],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: Wishlist[] }>('/wishlists');
      return res.data.data;
    },
  });
}

export function useCreateWishlist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const res = await api.post<{ success: boolean; data: Wishlist }>('/wishlists', { name });
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlists'] });
    },
  });
}

export function useUpdateWishlist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const res = await api.put<{ success: boolean; data: Wishlist }>(`/wishlists/${id}`, { name });
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlists'] });
    },
  });
}

export function useWishlistDetail(id: string) {
  return useQuery({
    queryKey: ['wishlists', id],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: WishlistDetail }>(`/wishlists/${id}`);
      return res.data.data;
    },
    enabled: !!id,
  });
}

export function useShareWishlist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ success: boolean; data: ShareResult }>(`/wishlists/${id}/share`);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlists'] });
    },
  });
}

export function useUnshareWishlist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/wishlists/${id}/share`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlists'] });
    },
  });
}

export function useDeleteWishlist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/wishlists/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlists'] });
    },
  });
}
